// ============================================
// Chat Module - Token Info Modal (ES6)
// 메시지별 토큰 사용량 상세 표시
// 단순화 버전
// ============================================

import { ModalManager } from '../modal-manager.js';
import { getModelOptions } from '../config.js';

/**
 * 모델 ID로 표시 이름 가져오기
 */
function getModelDisplayName(modelId) {
  if (!modelId || modelId === 'unknown') return '알 수 없음';
  const models = getModelOptions();
  const model = models.find(m => m.id === modelId);
  return model ? model.name : modelId;
}

/**
 * 시간을 초 단위 문자열로 변환
 */
function formatTime(ms) {
  if (!ms || ms <= 0) return '0s';
  return (ms / 1000).toFixed(2) + 's';
}

/**
 * 토큰 정보 모달 열기
 * @param {Object} tokenUsage - 토큰 사용량 데이터
 */
export function showTokenInfoModal(tokenUsage) {
  closeTokenInfoModal();

  if (!tokenUsage) {
    console.warn('[TokenInfo] tokenUsage가 없습니다');
    return;
  }

  // 디버깅용 로그
  console.log('[TokenInfo] 데이터:', tokenUsage);

  const modal = document.createElement('div');
  modal.id = 'tokenInfoModal';
  modal.className = 'bottom-sheet-overlay';

  // 데이터 추출
  const inputTokens = tokenUsage.input || 0;
  const thinkingTokens = tokenUsage.thinking || 0;
  const outputTokens = (tokenUsage.output || 0) + thinkingTokens;  // 생각 토큰 포함
  const cachedTokens = tokenUsage.cached || 0;
  const usedTokens = inputTokens - cachedTokens;  // 캐시 안된 토큰
  const modelId = tokenUsage.model || 'unknown';
  const timing = tokenUsage.timing || { firstToken: 0, total: 0 };
  const cost = tokenUsage.cost || { carbon: 0, krw: 0 };

  // 모델 표시 이름
  const modelName = getModelDisplayName(modelId);

  // 입력 토큰 그래프 비율 (캐시/비캐시)
  const cachedPercent = inputTokens > 0 ? (cachedTokens / inputTokens) * 100 : 0;
  const usedPercent = 100 - cachedPercent;

  // 타이밍 비율 계산
  const firstTokenTime = timing.firstToken || 0;
  const totalTime = timing.total || 1;
  const generationTime = totalTime - firstTokenTime;
  const firstTokenPercent = totalTime > 0 ? (firstTokenTime / totalTime) * 100 : 0;

  modal.innerHTML = `
    <div class="bottom-sheet-backdrop" data-action="close"></div>
    <div class="bottom-sheet token-info-sheet-v2">
      <div class="bottom-sheet-handle"></div>
      <div class="bottom-sheet-body">

        <!-- 입력 토큰 섹션 -->
        <div class="token-section">
          <div class="token-section-header">
            <span class="token-section-title">입력 토큰</span>
            <span class="token-section-value">${inputTokens.toLocaleString()}</span>
          </div>
          <div class="token-input-bar">
            <div class="token-input-used" style="width: ${usedPercent}%"></div>
            <div class="token-input-cached" style="width: ${cachedPercent}%"></div>
          </div>
          <div class="token-input-legend">
            <span class="token-input-item used">
              <span class="token-input-dot"></span>
              사용: ${usedTokens.toLocaleString()}
            </span>
            <span class="token-input-item cached">
              <span class="token-input-dot"></span>
              캐시: ${cachedTokens.toLocaleString()}
            </span>
          </div>
        </div>

        <!-- 출력 토큰 섹션 -->
        <div class="token-section">
          <div class="token-section-header">
            <span class="token-section-title">출력 토큰</span>
            <span class="token-section-value">${outputTokens.toLocaleString()}</span>
          </div>
        </div>

        <!-- 모델 섹션 -->
        <div class="token-section">
          <div class="token-section-header">
            <span class="token-section-title">모델</span>
            <span class="token-model-badge">${modelName}</span>
          </div>
        </div>

        <!-- 생성시간 섹션 -->
        <div class="token-section">
          <div class="token-section-header">
            <span class="token-section-title">전체 생성시간</span>
            <span class="token-section-value">${formatTime(totalTime)}</span>
          </div>
          <div class="token-timing-bar">
            <div class="token-timing-first" style="width: ${Math.min(firstTokenPercent, 100)}%"></div>
            <div class="token-timing-gen" style="width: ${Math.max(100 - firstTokenPercent, 0)}%"></div>
          </div>
          <div class="token-timing-legend">
            <span class="token-timing-item first">
              <span class="token-timing-dot"></span>
              첫 토큰: ${formatTime(firstTokenTime)}
            </span>
            <span class="token-timing-item gen">
              <span class="token-timing-dot"></span>
              생성: ${formatTime(generationTime)}
            </span>
          </div>
        </div>

        <!-- 비용 섹션 -->
        <div class="token-section cost-section">
          <div class="token-section-header">
            <span class="token-section-title">
              <span class="token-cost-icon">💎</span>
              총 비용
            </span>
            <span class="token-cost-value">${cost.carbon}</span>
          </div>
          <div class="token-cost-krw">약 ${cost.krw}원</div>
        </div>

      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // 이벤트 핸들러
  modal.addEventListener('click', (e) => {
    if (e.target.closest('[data-action="close"]')) {
      closeTokenInfoModal();
    }
  });

  // ModalManager 등록
  ModalManager.register('tokenInfoModal', closeTokenInfoModal, 'bottomSheet');
  ModalManager.push('tokenInfoModal');

  // 애니메이션
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add('open');
    });
  });
}

/**
 * 토큰 정보 모달 닫기
 */
export function closeTokenInfoModal() {
  const modal = document.getElementById('tokenInfoModal');
  if (modal) {
    modal.classList.remove('open');
    setTimeout(() => modal.remove(), 300);
    ModalManager.remove('tokenInfoModal', true);
    ModalManager.unregister('tokenInfoModal');
  }
}
